/**
 * Practice portfolio (P5) — the multi-practice landing view. Cards double as
 * the practice selector: selecting here uses EXACTLY the top-bar selector's
 * mechanism (AuthContext setters + the three session keys), never a parallel
 * store. (docs/reference/app-plus/IMPLEMENTATION_PLAN.md, P5)
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, UploadCloud } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { computeUploadAlert, daysAgoLabel } from './overview-logic';

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface PortfolioCardData {
  composite: number | null;
  lastUploadAt: string | null;
}

export function PracticePortfolio({
  practices,
  clientAlias,
  onSelect,
}: {
  practices: Practice[];
  clientAlias: string;
  onSelect: (practice: Practice) => void;
}) {
  const { programYear } = useProgramYear();
  const [cards, setCards] = useState<Record<number, PortfolioCardData>>({});
  const now = new Date();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(practices.map(async (p) => {
        let composite: number | null = null;
        let lastUploadAt: string | null = null;
        try {
          const scores = await apiClient.get(`/api/practices/${p.id}/mips/scores?year=${programYear}`);
          const c = Number(scores?.composite_mips_score);
          composite = Number.isFinite(c) ? c : null;
        } catch { /* card degrades */ }
        try {
          const uploads = await apiClient.get(`/api/claims/upload-history?practiceId=${p.id}`);
          const completed = (Array.isArray(uploads) ? uploads : [])
            .filter((u: { status?: string }) => u.status === 'completed');
          lastUploadAt = completed.length ? completed[0].uploadDate ?? null : null;
        } catch { /* card degrades */ }
        return [p.id, { composite, lastUploadAt }] as const;
      }));
      if (!cancelled) setCards(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [practices, programYear]);

  return (
    <div>
      <header className="mb-8">
        <h1 className="text-3xl font-bold">Practice Portfolio</h1>
        <p className="mt-2 text-muted-foreground">
          You are working under <span className="font-semibold text-primary">{clientAlias}</span>.
          Select a practice to open its overview.
        </p>
      </header>
      <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {practices.map((practice) => {
          const data = cards[practice.id];
          const alert = data ? computeUploadAlert(data.lastUploadAt, now) : null;
          return (
            <Card
              key={practice.id}
              className="flex cursor-pointer flex-col transition-shadow hover:shadow-md"
              role="button"
              aria-label={`Open ${practice.practice_alias || practice.tin_name}`}
              onClick={() => onSelect(practice)}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {practice.practice_alias || practice.tin_name}
                    </CardTitle>
                    <CardDescription>TIN: {practice.tin}</CardDescription>
                  </div>
                  {data?.composite != null && (
                    <div className="text-right">
                      <div className="text-2xl font-bold leading-none">{data.composite.toFixed(1)}</div>
                      <div className="text-xs text-muted-foreground">MIPS {programYear}</div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="mt-auto flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <UploadCloud className="h-3.5 w-3.5" />
                  {data ? daysAgoLabel(data.lastUploadAt, now) : '…'}
                </span>
                {alert === 'no_data' && <Badge variant="outline">No uploads yet</Badge>}
                {alert === 'stale_upload' && <Badge variant="destructive">Data stale</Badge>}
              </CardContent>
            </Card>
          );
        })}
      </section>
    </div>
  );
}
